<script setup name="TVBVideoDetail">
import { inject, ref, provide, onMounted, onServerPrefetch, computed, nextTick, watch } from 'vue'
import tvbBreadcrumb from '@/components/TvbBreadcrumb/index.vue'
import TvbVideoDetailContainer from './components/Container/index.vue'
import TvbVideoDetailInfo from './components/Info/index.vue'
import TvbPreferredSources from '@/components/TvbPreferredSources/index.vue'
import { useRoute } from 'vue-router'
import { getVideoDetail, getVideoPreview } from '@/api/video'
import { NMessageProvider } from 'naive-ui'
import { Head } from '@unhead/vue/components'
import { useHead } from '@unhead/vue'

const route = useRoute()
const currentVideo = ref({})
const defaultVideo = ref({})
const nextVideo = ref({})
// 上一集视频状态，用于播放器上一集按钮功能
const prevVideo = ref({})
const language = inject('language', 'zh')
const loading = ref(true)
const isPreview = inject('isPreview')
const pageCode = ref(0)
const seoTitleSuffix = inject('seoTitleSuffix')
const size = inject('size')
// 移动端（s/xs）：追踪按钮不放面包屑行，改由 Info 组件置于發佈日期下方
const isMobile = computed(() => ['s', 'xs'].includes(size.value))

// 广告相关计算属性
const videoId = computed(() => currentVideo.value?.id)
const categoryInfo = computed(() => currentVideo.value?.category_info)
const isShowAd = computed(() => currentVideo.value?.is_show_ad ?? 1)

// 广告加载状态管理，用于通知 Container 组件重新计算播放器尺寸
const adsLoaded = ref(false)
const onAdLoad = () => {
  console.log('[Shaka Player] 广告加载完成，触发播放器尺寸重新计算')
  adsLoaded.value = true
}

// 视频扩展信息（含 category_info）是否已加载完成
// 用于延迟渲染 topbn/to/lrec/sb/bn 等广告位，避免 TvbAds 在 loadAd 时快照到 'x'
// 修复：只有 PR/MR 在 getExtra 完成后才请求广告，其他广告位会因快照过早而拿到 'x'
const extraLoaded = ref(false)

// 监听广告显示状态变化，如果广告被屏蔽则直接设置为已加载
watch(
  () => isShowAd.value,
  (showAd) => {
    if (showAd === 2) {
      console.log('[Shaka Player] 广告被屏蔽，跳过广告加载等待')
      adsLoaded.value = true
    }
  },
  { immediate: true }
)

provide('currentVideo', currentVideo)
provide('adsLoaded', adsLoaded)
// 暴露 extraLoaded 给 Player/Container/Info 子组件使用
// Player 在 getExtra 完成后置 true；Container/Info 用于 gate 非PR/MR广告位的渲染
provide('extraLoaded', extraLoaded)
provide('defaultVideo', defaultVideo)
provide('setCurrentVideo', (video) => {
  // 切换剧集时重置扩展信息标志，让 topbn/to/lrec 等广告位先卸载，
  // 等 Player 的 getExtra 完成（category_info.path 就绪）后再重新渲染刷新广告
  extraLoaded.value = false
  currentVideo.value = video
})

// 注入全局广告参数方法（由 layout 提供，用于 splash/wsky 广告）
// 确保 splash 广告的 cust_params 携带 video_id 与 content_type=video（修复 TC-1607）
const setAdParams = inject('setAdParams', () => {})

// 监听视频数据与分类信息变化，同步注入广告参数到全局 layout
// - video_id 优先使用 /api/video/xxx 返回的 id 值
// - category_info 由 Player 组件 getExtra 异步补充，故同时监听其 path
// - 增加 extraLoaded 条件：等 getExtra 完成（category_info.path 就绪）后再注入
//   避免 splash 等依赖 adParams 的广告在 path 未就绪时快照到 'x'（修复 sub_category_path=x 问题）
watch(
  [() => currentVideo.value?.id, () => currentVideo.value?.category_info?.path, extraLoaded],
  ([id, path, loaded]) => {
    if (!id || !loaded) return
    setAdParams({
      content_type: 'video',
      vtype: 'vod',
      sub_category_path: path || 'x',
      video_id: id
    })
  },
  { immediate: true }
)
provide('nextVideo', nextVideo)
provide('setNextVideo', (video) => {
  nextVideo.value = video
})
// 提供上一集视频状态和方法给子组件使用
provide('prevVideo', prevVideo)
provide('setPrevVideo', (video) => {
  prevVideo.value = video
})

const getCurrentVideo = async () => {
  // 路由切换或重新拉取视频时，重置扩展信息加载标志
  // 确保 topbn/to/lrec/sb/bn 等广告位不会在 getExtra 完成前提前渲染拿到 'x'
  extraLoaded.value = false
  const videoId = route.params?.videoId?.replace('v', '')
  if (videoId) {
    const apiFun = isPreview.value ? getVideoPreview : getVideoDetail
    const { code, data } = await apiFun({ video_id: videoId })
    pageCode.value = code
    data.category_info = {}
    if (code === 0) {
      defaultVideo.value = data
      currentVideo.value = defaultVideo.value
    }
  }
  loading.value = false
  if (typeof window !== 'undefined') {
    nextTick(() => {
      window.scroll(0, 0)
    })
  }
}

onMounted(() => {
  getCurrentVideo()
})
onServerPrefetch(getCurrentVideo)

const breadcrumbs = computed(() => {
  const breadcrumbs = [
    {
      name: language === 'en' ? 'Home' : '主頁',
      path: '/'
    }
  ]
  if (currentVideo.value?.category_info?.parent?.title) {
    breadcrumbs.push({
      name: currentVideo.value?.category_info?.parent?.title || '',
      path: currentVideo.value?.category_info?.parent?.link || ''
    })
  }
  if (currentVideo.value?.category_info?.title) {
    breadcrumbs.push({
      name: currentVideo.value?.category_info?.title || '',
      path: currentVideo.value?.category_info?.link || ''
    })
  }
  return breadcrumbs
})

const ogImageUrl = computed(() => currentVideo?.value?.og_image_info?.medium || '')

const processedDescription = computed(() => {
  const description = currentVideo.value?.meta_description || currentVideo.value?.description || ''
  const plainText = description.replace(/<[^>]*>/g, '')
  const cleanedText = plainText.replace(/\s+/g, ' ').trim()
  return cleanedText.length > 160 ? cleanedText.slice(0, 160) : cleanedText
})

const author = computed(() => currentVideo.value.author?.join('、'))
const VITE_HOST_URL = import.meta.env.VITE_HOST_URL
const canonical = computed(() => VITE_HOST_URL + (currentVideo?.value?.uri?.slice(1) || ''))
const seoTitle = computed(
  () => (currentVideo.value?.seo_title || currentVideo.value?.title || '') + seoTitleSuffix
)
const extraLdJsonScript = computed(() => {
  const ldJson = currentVideo.value?.ld_json
  if (!ldJson) return ''
  if (typeof ldJson === 'string') {
    const trimLdJson = ldJson.trim()
    if (!trimLdJson) return ''
    try {
      JSON.parse(trimLdJson)
      return trimLdJson
    } catch (error) {
      return ''
    }
  }
  try {
    return JSON.stringify(ldJson)
  } catch (error) {
    return ''
  }
})
const scriptData = computed(() => {
  const script = []
  if (extraLdJsonScript.value) {
    script.push({
      id: 'video-extra-ld-json',
      innerHTML: extraLdJsonScript.value,
      type: 'application/ld+json'
    })
  }
  return { script }
})
const { patch } = useHead(scriptData)

watch(
  scriptData,
  () => {
    patch(scriptData.value)
  },
  { immediate: true }
)
</script>

<template>
  <div class="tvb-video-page" :class="[size]">
    <!-- Top Banner 广告：位于面包屑上方 -->
    <!-- 加 extraLoaded 条件：等 Player 的 getExtra 完成（category_info.path 就绪）后再渲染 -->
    <tvb-ads
      v-if="videoId && isShowAd !== 2 && extraLoaded"
      ad-type="topbn"
      :position="14"
      :unit="`/${videoId}`"
      :show-ad="isShowAd !== 2"
      :params="{
        content_type: 'video',
        vtype: 'vod',
        sub_category_path: categoryInfo?.path || 'x',
        video_id: videoId || 'x'
      }"
      @load="onAdLoad"
      @error="onAdLoad"
    />
    <div class="tvb-video-page_nav" v-show="breadcrumbs && !loading">
      <tvb-breadcrumb :breadcrumbs="breadcrumbs" />
      <tvb-preferred-sources v-if="!isMobile" />
    </div>
    <div class="tvb-video-detail" v-if="!loading">
      <tvb-video-detail-container>
        <n-message-provider>
          <tvb-video-detail-info :key="currentVideo?.id" />
        </n-message-provider>
      </tvb-video-detail-container>
    </div>

    <!-- Takeover 广告 - 视频详情页 -->
    <!-- 加 extraLoaded 条件：等 Player 的 getExtra 完成（category_info.path 就绪）后再渲染 -->
    <tvb-ads
      v-if="videoId && isShowAd !== 2 && extraLoaded"
      ad-type="to"
      :position="14"
      :unit="`/${videoId}`"
      :show-ad="isShowAd !== 2"
      :params="{
        content_type: 'video',
        vtype: 'vod',
        sub_category_path: categoryInfo?.path || 'x',
        video_id: videoId || 'x'
      }"
    />

    <tvb-empty
      v-if="pageCode !== 0"
      icon="img_article_deleted"
      :des="isPreview ? '預覽鏈接已失效，請重新獲取' : '抱歉，找不到相關資料'"
      :showbtn="!isPreview"
      style="margin: 12vh auto 6vh"
    ></tvb-empty>
    <Head>
      <title>{{ seoTitle }}</title>
      <meta name="author" :content="author" />
      <meta name="description" :content="processedDescription" />
      <meta property="og:type" content="video.other" />
      <meta property="og:image" :content="ogImageUrl" />
      <meta property="og:title" :content="seoTitle" />
      <meta property="og:image:alt" :content="currentVideo.og_image_alt_txt" />
      <meta property="og:url" :content="canonical" />
      <meta property="og:description" :content="processedDescription" />
      <meta name="twitter:title" :content="seoTitle" />
      <meta name="twitter:image" :content="ogImageUrl" />
      <meta name="twitter:image:alt" :content="currentVideo.og_image_alt_txt" />
      <meta name="twitter:url" :content="canonical" />
      <meta property="twitter:description" :content="processedDescription" />
    </Head>
  </div>
</template>

<style scoped lang="scss">
.tvb-video-page {
  &.m {
    :deep(.tvb-ads[data-type*='topbanner']:not(.zero)) {
      margin-bottom: 16px;
    }
  }
  &.s,
  &.xs {
    .tvb-video-detail {
      margin-top: 8px;
    }
  }
  // TC-1481：页面顶部间距 16px → 8px（对齐设计稿，与 s/xs 分支统一）
  .tvb-video-detail {
    margin-top: 8px;
  }

  .tvb-video-page_nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    width: calc(var(--player-width, 400px));

    @media (max-width: 1144px) {
      width: 100%;
    }
  }

  :deep(.tvb-breadcrumbs) {
    @media (min-width: 1144px) {
      // 面包屑宽度 = 播放器宽度 + 播放列表 400px + 播放器与播放列表固定间距 24px
      // （原 16px 为旧间距，与 Info 区 width 公式中的 24px 对齐）
      width: calc(var(--player-width, 400px));
      margin: 0 auto;
    }
  }
}

// TC-1481/TC-1482：≥1144px 时页面内容与底部间距 80px
// .tvb-video-page 为路由根元素（router-view 的 .tvb-main 类会透传至该节点），
// 此处覆盖全局 .tvb-main 在 xl（≥1441px）下的 100px 底部内边距，统一为设计稿 80px；
// 1144-1440px 的 l/xl 档原本即为 80px，视觉不变。附加 .tvb-container 提高选择器权重，保证稳定覆盖全局样式
@media (min-width: 1144px) {
  .tvb-container .tvb-video-page.tvb-main {
    padding-bottom: 80px;
  }
}
</style>
